import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { AudienceFields, type AudienceForm, type Group, type GroupFieldName } from '../../../types';
import { isGroupReallyEmpty } from '../../../utils/isGroupReallyEmpty';
import { getCountriesFromGroup, getRegionFromGroup, isGroup } from '../components/utils/guards';
import { getGroupFromPath } from '../utils/getGroupFromPath';

export type UseMarketSectionProps = {
  groupFieldName: GroupFieldName;
  update: (index: number, value: Group) => void;
  index: number;
};

export type UseMarketSectionResponse = {
  selectedRegionCode: string | null;
  setSelectedRegion: (code: string | null) => void;
  selectedCountries: string[];
  setSelectedCountries: (countries: string[] | null) => void;
  isRegionSelected: boolean;
  clearRegion: () => void;
  hasSegments: boolean;
  changeRegionAndClearSegments: (code: string | null) => void;
};

export const useMarketSection = ({
  groupFieldName,
  update,
  index,
}: UseMarketSectionProps): UseMarketSectionResponse => {
  const { control } = useFormContext<AudienceForm>();

  const observableNormalizedDefinition = useWatch({
    name: AudienceFields.NormalizedDefinition,
    control,
  });

  const watchedGroup = useMemo(
    () => getGroupFromPath(observableNormalizedDefinition, groupFieldName),
    [observableNormalizedDefinition, groupFieldName]
  );

  const selectedRegionCode = useMemo(() => {
    if (!isGroup(watchedGroup)) return null;

    return getRegionFromGroup(watchedGroup);
  }, [watchedGroup]);

  const selectedCountries = useMemo(() => {
    if (!isGroup(watchedGroup)) return [];

    return getCountriesFromGroup(watchedGroup);
  }, [watchedGroup]);

  const updateGroup = useCallback(
    (patch: { region?: string | null; countries?: string[] | null }) => {
      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      const currentRegion = getRegionFromGroup(currentGroup);
      const currentCountries = getCountriesFromGroup(currentGroup);
      const nextRegion = patch.region !== undefined ? patch.region : currentRegion;
      const nextCountries =
        patch.countries !== undefined ? patch.countries : nextRegion == null ? null : (currentCountries ?? null);

      const nextGroup: Group = {
        ...currentGroup,
        region: nextRegion ?? null,
        countries: nextRegion == null ? null : nextCountries,
      };

      update(index, nextGroup);
    },
    [update, index, watchedGroup]
  );

  const setSelectedRegion = useCallback(
    (code: string | null) => {
      updateGroup({ region: code ?? null, countries: null });
    },
    [updateGroup]
  );

  const setSelectedCountries = useCallback(
    (countries: string[] | null) => {
      if (selectedRegionCode == null) return;
      updateGroup({ countries: countries ?? null });
    },
    [selectedRegionCode, updateGroup]
  );

  const isRegionSelected = selectedRegionCode != null && selectedRegionCode !== '';

  const hasSegments = useMemo(
    () => (isGroup(watchedGroup) ? !isGroupReallyEmpty(watchedGroup) : false),
    [watchedGroup]
  );

  const changeRegionAndClearSegments = useCallback(
    (code: string | null) => {
      const currentGroup = watchedGroup;

      if (!isGroup(currentGroup)) return;

      const { uuid, operator } = currentGroup;
      const nextGroup: Group = {
        uuid,
        operator,
        region: code ?? null,
        countries: null,
      };
      update(index, nextGroup);
    },
    [update, index, watchedGroup]
  );

  const clearRegion = useCallback(() => {
    changeRegionAndClearSegments(null);
  }, [changeRegionAndClearSegments]);

  return {
    selectedRegionCode,
    setSelectedRegion,
    selectedCountries,
    setSelectedCountries,
    isRegionSelected,
    clearRegion,
    hasSegments,
    changeRegionAndClearSegments,
  };
};
